import { createReducer, Action, on } from '@ngrx/store';

import { AppActions } from './app.actions';
import { AppState } from './app.state';
import { HelpComponentMode } from '../../features/help/models/help.model';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { UserProfileActions } from '../user-profile/user-profile.actions';

export const initialState: AppState = {
  printMode: false,
  helpOpen: false,
  helpContextual: true,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      AppActions.LayoutChanged,
      (state, action): AppState => ({ ...state, breakPointState: action.breakpointState })
    ),
    on(
      AppActions.SetExtended,
      (state, action): AppState => ({
        ...state,
        sidenavExtended: action.sidenavExtended ? 'open' : 'closed',
      })
    ),
    on(AppActions.SetUrl, (state, action): AppState => ({ ...state, url: action.url })),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): AppState => ({
        ...state,
        libraries: action.data.libraries,
        availableAssets: action.data.assets || [],
        initialized: true,
        appEnabled: true,
      })
    ),
    on(AppActions.LoadInitData, (state): AppState => ({ ...state, libraries: undefined })),
    on(
      AppActions.LoadBuiltInChecklistsSuccess,
      (state, action): AppState => ({
        ...state,
        builtInChecklistTopicIdsByAsset: action.topicIdsByAsset,
      })
    ),
    on(AppActions.EnterOfflineModeSuccess, (state): AppState => ({ ...state, isOffline: true })),
    on(AppActions.RestoreFromOfflineMode, (state): AppState => ({ ...state, isOffline: false })),
    on(
      UserProfileActions.UpdateAsset,
      (state, action): AppState => ({
        ...state,
        selectedAssetShortNames: action.selectedAssetShortNames || [],
      })
    ),
    on(
      UserProfileActions.LoadProfileSuccess,
      (state, action): AppState => ({
        ...state,
        selectedAssetShortNames:
          (action.init ? action.selectedAssetShortName : state.selectedAssetShortNames) || [],
        availableAssets:
          action.init && !!action.selectedAssetShortName && !!action.profile?.permissions?.length
            ? state.availableAssets
            : [],
        appEnabled: true,
      })
    ),
    on(
      AppActions.SetAppEnabled,
      (state, action): AppState => ({ ...state, appEnabled: action.appEnabled })
    ),
    on(
      AppActions.SetPrintMode,
      (state, action): AppState => ({
        ...state,
        printMode: action.isPrintMode,
        printDate: action.isPrintMode ? new Date() : undefined,
      })
    ),
    on(AppActions.ToggleHelp, (state, action): AppState => {
      return {
        ...state,
        helpOpen: action.open,
        helpId: action.helpId || state.helpId,
        helpContextual: action.contextual ?? state.helpContextual,
        conversationBarOpen: action.open ? false : state.conversationBarOpen,
        helpMode: action.helpMode ?? HelpComponentMode.Help,
      };
    }),
    on(AppActions.ToggleConversationSidebar, (state, action): AppState => {
      const conversationBarOpen =
        action.open !== undefined ? action.open : !state.conversationBarOpen;
      return {
        ...state,
        conversationBarOpen,
        helpOpen: conversationBarOpen ? false : state.helpOpen,
      };
    }),
    on(
      AppActions.DisableConversationSidebar,
      (state, action): AppState => ({
        ...state,
        conversationBarDisabled: action.disabled,
        conversationBarOpen: false,
      })
    ),
    on(
      AppActions.SetContextualHelp,
      (state, action): AppState => ({ ...state, helpContextual: action.enabled })
    ),
    on(AppActions.SetHelpId, (state, action): AppState => ({ ...state, helpId: action.helpId })),
    on(
      AppActions.LoadSynonymClassesSuccess,
      (state, action): AppState => ({ ...state, synonymClassesByAsset: action.synonymClassesByAsset }),
    ),
    on(AppActions.LoadSystemMessagesSuccess, (state, action): AppState => ({ ...state, systemMessages: action.systemMessages })),
  )
);

export function reducer(state: AppState | undefined, action: Action) {
  return featureReducer(state, action);
}
